// AnalyticsSceneDOM.js - Complete Analytics Dashboard with Template Implementation
// Integrates desktop-analytics-dashboard.html + mobile-analytics-view.html templates

import BaseMenuScene from "./BaseMenuScene.js";
import { auth } from "../firebaseConfig.js";
import { createSidebarNavigation } from "../components/SharedSidebarNavigation.js";
import templateOverlayManager from "../utils/TemplateOverlayManager.js";
import { baseSceneIntegration } from "../services/BaseSceneIntegration.js";
import { addCriticalNavigationCSS } from "../utils/CriticalNavigationCSS.js";

export default class AnalyticsSceneDOM extends BaseMenuScene {
    constructor() {
        super({ key: "AnalyticsSceneDOM" });
        this.domElement = null;
        this.isMobile = window.innerWidth <= 768;
        this.analyticsData = null;
        this.currentPeriod = 'week';
    }

    async create() {
        console.log('📊 AnalyticsSceneDOM: Creating complete analytics dashboard with templates');
        
        // Load analytics data
        await this.loadAnalyticsData();
        
        // Load EXACT same CSS as Dashboard and add Analytics content styling
        this.loadAnalyticsCSS();
        
        // Create the analytics interface with PPT Hero placeholder
        this.createPPTHeroPlaceholderInterface();
        
        // Initialize with enterprise services (replaces duplicate event binding)
        baseSceneIntegration.initializeTemplateScene(this, {
            overlayId: 'auth-menu-dom-overlay',
            bindEvents: true,
            enableNavigation: true
        });
        
        this.sceneReady = true;
    }

    async loadAnalyticsData() {
        try {
        // PPT Hero analytics placeholder - future project-wide system
        this.analyticsData = {
            overview: {
                totalSessions: 26,
                bestScore: 83500,
                globalRank: 1,
                accuracy: 85,
                weeklyGrowth: 12,
                category: "PPT Hero",
                note: "Comprehensive project-wide analytics coming soon"
            },
                performance: {
                    weeklyScores: [2800, 3200, 3500, 3800, 4100, 4300, 4500],
                    accuracyTrend: [85, 87, 89, 92, 94, 95, 94],
                    speedTrend: [75, 78, 82, 85, 87, 89, 87],
                    categories: {
                        universal: { score: 100, sessions: 20 },
                        firmBasic: { score: 90, sessions: 15 },
                        firmAdvanced: { score: 75, sessions: 8 }
                    }
                },
                games: {
                    pptHero: {
                        totalSessions: 26,
                        bestScore: 83500,
                        averageScore: 3141,
                        progression: 'PPT Hero',
                        weeklyActivity: 5
                    },
                    philosophyFlow: {
                        totalSessions: 3,
                        bestScore: 0,
                        averageScore: 0,
                        progression: 'Getting Started',
                        weeklyActivity: 1
                    }
                },
                insights: [
                    {
                        type: 'achievement',
                        title: 'Consistency Champion',
                        description: 'You\'ve maintained daily practice for 7 days straight!',
                        impact: 'high'
                    },
                    {
                        type: 'improvement',
                        title: 'Speed Optimization',
                        description: 'Your execution speed improved 23% this week.',
                        impact: 'medium'
                    }
                ]
            };
        } catch (error) {
            console.error('Error loading analytics data:', error);
            this.analyticsData = this.getDefaultAnalyticsData();
        }
    }

    getDefaultAnalyticsData() {
        return {
            overview: { totalSessions: 0, averageScore: 0, bestScore: 0, globalRank: 0, firmRank: 0 },
            performance: { weeklyScores: [], accuracyTrend: [], speedTrend: [] },
            games: {},
            insights: []
        };
    }

    createPPTHeroPlaceholderInterface() {
        // CRITICAL: Add global critical CSS for all navigation
        addCriticalNavigationCSS();
        
        // Remove existing overlay
        const existingOverlay = document.getElementById('auth-menu-dom-overlay');
        if (existingOverlay) {
            existingOverlay.remove();
        }

        const overlay = document.createElement('div');
        overlay.id = 'auth-menu-dom-overlay';  // SAME ID as Dashboard
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            z-index: 10000;
            overflow: hidden;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: rgba(255, 255, 255, 0.9);
            box-sizing: border-box;
        `;

        // Load analytics CSS (already called in create method)

        if (this.isMobile) {
            overlay.innerHTML = this.createMobileAnalyticsHTML();
        } else {
            overlay.innerHTML = this.createDesktopAnalyticsHTML();
        }

        document.body.appendChild(overlay);
        this.domElement = overlay;
        
        // Register with overlay manager for proper cleanup
        templateOverlayManager.registerOverlay('auth-menu-dom-overlay', 'AnalyticsSceneDOM');
    }


    loadAnalyticsCSS() {
        console.log('🎨 Loading Analytics CSS...');
        
        // Load EXACT same CSS as Dashboard
        if (!document.getElementById('desktop-executive-dashboard-exact-css')) {
            const link = document.createElement('link');
            link.id = 'desktop-executive-dashboard-exact-css';
            link.rel = 'stylesheet';
            link.href = '/desktop-executive-dashboard-exact.css'; // NO query parameter
            link.onload = () => console.log('✅ Desktop template CSS loaded');
            link.onerror = () => console.warn('⚠️ Desktop template CSS failed - using critical CSS fallback');
            document.head.appendChild(link);
        }
        
        // Add Analytics content styling
        this.addAnalyticsContentCSS();
        
        console.log('🎨 All Analytics CSS loaded');
    }

    addAnalyticsContentCSS() {
        const contentStyle = document.createElement('style');
        contentStyle.id = 'analytics-content-css';
        contentStyle.textContent = `
            /* Analytics Content Styling - EXACT Template Structure */
            
            /* Header styling */
            #auth-menu-dom-overlay .header {
                background: rgba(30, 41, 59, 0.6) !important;
                backdrop-filter: blur(10px) !important;
                border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
                padding: 0 32px !important;
                height: 70px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: space-between !important;
                margin: 0 !important;
            }
            
            /* Reduce content padding to eliminate gap */
            #auth-menu-dom-overlay .dashboard-content {
                padding: 24px 32px !important;
            }
            
            /* KPI Grid - EXACT template structure */
            #auth-menu-dom-overlay .kpi-grid {
                display: grid !important;
                grid-template-columns: repeat(4, 1fr) !important;
                gap: 24px !important;
                margin-bottom: 32px !important;
            }
            
            #auth-menu-dom-overlay .kpi-card {
                background: rgba(30, 41, 59, 0.6) !important;
                backdrop-filter: blur(20px) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
                border-radius: 20px !important;
                padding: 24px !important;
                transition: all 0.3s ease !important;
            }
            
            #auth-menu-dom-overlay .kpi-header {
                display: flex !important;
                align-items: center !important;
                justify-content: space-between !important;
                margin-bottom: 16px !important;
            }
            
            #auth-menu-dom-overlay .kpi-label {
                font-size: 14px !important;
                font-weight: 600 !important;
                color: var(--text-secondary) !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
            }
            
            #auth-menu-dom-overlay .kpi-icon {
                font-size: 20px !important;
            }
            
            #auth-menu-dom-overlay .kpi-value {
                font-size: 32px !important;
                font-weight: 700 !important;
                color: var(--accent-primary) !important;
                margin-bottom: 8px !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                letter-spacing: -0.5px !important;
            }
            
            #auth-menu-dom-overlay .kpi-change {
                font-size: 12px !important;
                font-weight: 500 !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
            }
            
            #auth-menu-dom-overlay .change-positive {
                color: var(--success) !important;
            }
            
            #auth-menu-dom-overlay .change-indicator {
                font-weight: 600 !important;
            }
            
            /* Charts Row */
            #auth-menu-dom-overlay .charts-row {
                display: grid !important;
                grid-template-columns: 2fr 1fr !important;
                gap: 32px !important;
                margin-bottom: 32px !important;
            }
            
            #auth-menu-dom-overlay .chart-card {
                background: rgba(30, 41, 59, 0.6) !important;
                backdrop-filter: blur(20px) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
                border-radius: 20px !important;
                padding: 24px !important;
            }
            
            #auth-menu-dom-overlay .chart-title {
                font-size: 18px !important;
                font-weight: 600 !important;
                color: var(--text-primary) !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                margin-bottom: 20px !important;
            }
            
            /* Overview Cards */
            #auth-menu-dom-overlay .overview-grid {
                display: grid !important;
                grid-template-columns: repeat(4, 1fr) !important;
                gap: 24px !important;
                margin-bottom: 32px !important;
            }
            
            #auth-menu-dom-overlay .overview-card {
                background: rgba(30, 41, 59, 0.6) !important;
                backdrop-filter: blur(20px) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
                border-radius: 20px !important;
                padding: 24px !important;
                transition: all 0.3s ease !important;
            }
            
            #auth-menu-dom-overlay .card-header {
                display: flex !important;
                justify-content: space-between !important;
                align-items: center !important;
                margin-bottom: 16px !important;
            }
            
            #auth-menu-dom-overlay .card-title {
                font-size: 14px !important;
                font-weight: 600 !important;
                color: var(--text-secondary) !important;
            }
            
            #auth-menu-dom-overlay .card-icon {
                font-size: 20px !important;
            }
            
            #auth-menu-dom-overlay .card-value {
                font-size: 36px !important;
                font-weight: 700 !important;
                color: var(--accent-primary) !important;
                margin-bottom: 8px !important;
            }
            
            #auth-menu-dom-overlay .card-change {
                font-size: 12px !important;
                font-weight: 600 !important;
            }
            
            #auth-menu-dom-overlay .card-change.positive {
                color: var(--success) !important;
            }
            
            /* Details Grid */
            #auth-menu-dom-overlay .details-grid {
                display: grid !important;
                grid-template-columns: 1fr 1fr !important;
                gap: 24px !important;
                margin-bottom: 32px !important;
            }
            
            #auth-menu-dom-overlay .detail-card {
                background: rgba(30, 41, 59, 0.6) !important;
                backdrop-filter: blur(20px) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
                border-radius: 20px !important;
                padding: 24px !important;
            }
            
            /* Insights */
            #auth-menu-dom-overlay .insights-section {
                margin-top: 32px !important;
            }
            
            #auth-menu-dom-overlay .insights-title {
                font-size: 24px !important;
                font-weight: 600 !important;
                margin-bottom: 24px !important;
                color: var(--text-primary) !important;
            }
            
            #auth-menu-dom-overlay .insights-grid {
                display: grid !important;
                grid-template-columns: repeat(2, 1fr) !important;
                gap: 24px !important;
            }
            
            #auth-menu-dom-overlay .insight-card {
                background: rgba(30, 41, 59, 0.6) !important;
                backdrop-filter: blur(20px) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
                border-radius: 16px !important;
                padding: 24px !important;
            }
        `;
        document.head.appendChild(contentStyle);
        console.log('🎨 Analytics content CSS added');
    }

    // REMOVED: 37 lines of duplicate navigation logic
    // Now handled by baseSceneIntegration.initializeTemplateScene()
    // Navigation actions are centralized in NavigationActionHandler service

    createDesktopAnalyticsHTML() {
        // Use EXACT same structure as Dashboard with Analytics content
        return `
            <!-- Animated Background (SAME as Dashboard) -->
            <div class="background-effects">
                <div class="bg-grid"></div>
                <div class="bg-glow bg-glow-1"></div>
                <div class="bg-glow bg-glow-2"></div>
            </div>
            
            <!-- Layout Container (SAME as Dashboard) -->
            <div class="app-container">
                <!-- Sidebar Navigation (Shared Component) -->
                ${sidebar}
                
                <!-- Main Content -->
                <main class="main-content">
                    <!-- Header with breadcrumb -->
                    <header class="header">
                        <div class="header-left">
                            <h1 class="page-title">Performance Analytics</h1>
                            <div class="breadcrumb">
                                <span>Home</span>
                                <span class="breadcrumb-separator">/</span>
                                <span>Analytics</span>
                            </div>
                        </div>
                        <div class="header-right">
                            <select class="period-selector" data-field="period">
                                <option value="week">This Week</option>
                                <option value="month">This Month</option>
                                <option value="quarter">This Quarter</option>
                                <option value="year">This Year</option>
                            </select>
                            <button class="export-btn" data-action="export">Export Data</button>
                        </div>
                    </header>
                    
                    <!-- Dashboard Content (EXACT template structure) -->
                    <div class="dashboard-content">
                        <!-- KPI Grid -->
                        <div class="kpi-grid">
                            <div class="kpi-card">
                                <div class="kpi-header">
                                    <span class="kpi-label">Total Points</span>
                                    <div class="kpi-icon">🎯</div>
                                </div>
                                <div class="kpi-value">2,847</div>
                                <div class="kpi-change change-positive">
                                    <span class="change-indicator">↑ 23%</span>
                                    <span>vs last month</span>
                                </div>
                            </div>
                            
                            <div class="kpi-card">
                                <div class="kpi-header">
                                    <span class="kpi-label">Games Played</span>
                                    <div class="kpi-icon">🎮</div>
                                </div>
                                <div class="kpi-value">47</div>
                                <div class="kpi-change change-positive">
                                    <span class="change-indicator">↑ 12</span>
                                    <span>vs last month</span>
                                </div>
                            </div>
                            
                            <div class="kpi-card">
                                <div class="kpi-header">
                                    <span class="kpi-label">Win Rate</span>
                                    <div class="kpi-icon">🏆</div>
                                </div>
                                <div class="kpi-value">85%</div>
                                <div class="kpi-change change-positive">
                                    <span class="change-indicator">↑ 5%</span>
                                    <span>vs last month</span>
                                </div>
                            </div>
                            
                            <div class="kpi-card">
                                <div class="kpi-header">
                                    <span class="kpi-label">Firm Ranking</span>
                                    <div class="kpi-icon">📈</div>
                                </div>
                                <div class="kpi-value">#3</div>
                                <div class="kpi-change change-positive">
                                    <span class="change-indicator">↑ 2</span>
                                    <span>positions gained</span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Charts Row -->
                        <div class="charts-row">
                            <!-- Performance Chart -->
                            <div class="chart-card">
                                <div class="chart-header">
                                    <h3 class="chart-title">Performance Trend</h3>
                                    <div class="chart-options">
                                        <div class="chart-option">📊</div>
                                        <div class="chart-option">⚙️</div>
                                    </div>
                                </div>
                                <div class="performance-chart">
                                    <div class="chart-grid">
                                        <div class="grid-line"><span class="grid-label">1000</span></div>
                                        <div class="grid-line"><span class="grid-label">750</span></div>
                                        <div class="grid-line"><span class="grid-label">500</span></div>
                                        <div class="grid-line"><span class="grid-label">250</span></div>
                                        <div class="grid-line"><span class="grid-label">0</span></div>
                                    </div>
                                    <!-- Chart visualization would go here -->
                                </div>
                            </div>
                            
                            <!-- Game Distribution -->
                            <div class="chart-card">
                                <div class="chart-header">
                                    <h3 class="chart-title">Game Distribution</h3>
                                </div>
                                <div class="donut-container">
                                    <div class="donut-center">
                                        <div class="donut-value">47</div>
                                        <div class="donut-label">Total Games</div>
                                    </div>
                                    <!-- Donut chart would go here -->
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Analytics Content -->
                    <div class="analytics-content">
                        <!-- Overview Cards -->
                        <div class="overview-grid">
                            ${this.createOverviewCards()}
                        </div>
                        
                        <!-- Performance Charts -->
                        <div class="charts-section">
                            <div class="chart-card">
                                <div class="chart-header">
                                    <h3 class="chart-title">Performance Trend</h3>
                                    <div class="chart-legend">
                                        <span class="legend-item">
                                            <span class="legend-color score"></span>
                                            <span>Score</span>
                                        </span>
                                        <span class="legend-item">
                                            <span class="legend-color accuracy"></span>
                                            <span>Accuracy</span>
                                        </span>
                                    </div>
                                </div>
                                <div class="chart-container">
                                    ${this.createPerformanceChart()}
                                </div>
                            </div>
                        </div>
                        
                        <!-- Detailed Analytics -->
                        <div class="details-grid">
                            ${this.createDetailedAnalytics()}
                        </div>
                        
                        <!-- AI Insights -->
                        <div class="insights-section">
                            <h3 class="insights-title">AI-Powered Insights</h3>
                            <div class="insights-grid">
                                ${this.createInsights()}
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        `;
    }

    createMobileAnalyticsHTML() {
        return `
            ${createTemplateHeader('Performance Analytics', ['Home', 'Dashboard', 'Analytics'])}
            
            <!-- Mobile Analytics Template Structure -->
            <header class="header">
                <button class="back-button" data-action="back">
                    <span>←</span>
                </button>
                <h1 class="header-title">Analytics</h1>
                <button class="period-btn" data-action="change-period">Week ↓</button>
            </header>
            
            <div class="content">
                <!-- Overview Stats -->
                <div class="overview-stats">
                    ${this.createMobileOverviewStats()}
                </div>
                
                <!-- Performance Chart -->
                <div class="chart-section">
                    <h3 class="section-title">Performance Trend</h3>
                    <div class="mobile-chart">
                        ${this.createMobileChart()}
                    </div>
                </div>
                
                <!-- Game Breakdown -->
                <div class="games-breakdown">
                    <h3 class="section-title">Game Performance</h3>
                    ${this.createMobileGameBreakdown()}
                </div>
                
                <!-- Quick Insights -->
                <div class="insights-section">
                    <h3 class="section-title">Key Insights</h3>
                    ${this.createMobileInsights()}
                </div>
            </div>
        `;
    }

    createOverviewCards() {
        const data = this.analyticsData.overview;
        return `
            <div class="overview-card">
                <div class="card-header">
                    <span class="card-icon">🎯</span>
                    <h4 class="card-title">Total Sessions</h4>
                </div>
                <div class="card-value">${data.totalSessions}</div>
                <div class="card-change positive">+${data.weeklyGrowth}% this week</div>
            </div>
            
            <div class="overview-card">
                <div class="card-header">
                    <span class="card-icon">⭐</span>
                    <h4 class="card-title">Best Score</h4>
                </div>
                <div class="card-value">${data.bestScore.toLocaleString()}</div>
                <div class="card-change neutral">Personal best</div>
            </div>
            
            <div class="overview-card">
                <div class="card-header">
                    <span class="card-icon">🏆</span>
                    <h4 class="card-title">Global Rank</h4>
                </div>
                <div class="card-value">#${data.globalRank}</div>
                <div class="card-change positive">Top 1%</div>
            </div>
            
            <div class="overview-card">
                <div class="card-header">
                    <span class="card-icon">📈</span>
                    <h4 class="card-title">Accuracy</h4>
                </div>
                <div class="card-value">${data.accuracy}%</div>
                <div class="card-change positive">+2.1% this week</div>
            </div>
        `;
    }

    createMobileOverviewStats() {
        const data = this.analyticsData.overview;
        return `
            <div class="stat-card-mobile">
                <div class="stat-icon">🎯</div>
                <div class="stat-value">${data.totalSessions}</div>
                <div class="stat-label">Sessions</div>
            </div>
            <div class="stat-card-mobile">
                <div class="stat-icon">⭐</div>
                <div class="stat-value">${(data.bestScore / 1000).toFixed(1)}k</div>
                <div class="stat-label">Best Score</div>
            </div>
            <div class="stat-card-mobile">
                <div class="stat-icon">🏆</div>
                <div class="stat-value">#${data.globalRank}</div>
                <div class="stat-label">Rank</div>
            </div>
        `;
    }

    createPerformanceChart() {
        // Simple ASCII chart representation - could be replaced with actual chart library
        const scores = this.analyticsData.performance.weeklyScores;
        const maxScore = Math.max(...scores);
        
        return `
            <div class="simple-chart">
                ${scores.map((score, index) => `
                    <div class="chart-bar">
                        <div class="bar-fill" style="height: ${(score / maxScore) * 100}%"></div>
                        <div class="bar-label">Day ${index + 1}</div>
                        <div class="bar-value">${score}</div>
                    </div>
                `).join('')}
            </div>
        `;
    }

    createMobileChart() {
        return this.createPerformanceChart(); // Same chart for mobile
    }

    createDetailedAnalytics() {
        return `
            <div class="detail-card">
                <div class="card-header">
                    <h4 class="card-title">Category Breakdown</h4>
                    <span class="card-icon">📋</span>
                </div>
                <div class="category-stats">
                    <div class="category-item">
                        <span class="category-name">Universal Skills</span>
                        <span class="category-score">100/100</span>
                        <div class="category-bar">
                            <div class="category-fill" style="width: 100%"></div>
                        </div>
                    </div>
                    <div class="category-item">
                        <span class="category-name">Firm Basic</span>
                        <span class="category-score">90/100</span>
                        <div class="category-bar">
                            <div class="category-fill" style="width: 90%"></div>
                        </div>
                    </div>
                    <div class="category-item">
                        <span class="category-name">Firm Advanced</span>
                        <span class="category-score">75/100</span>
                        <div class="category-bar">
                            <div class="category-fill" style="width: 75%"></div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="detail-card">
                <div class="card-header">
                    <h4 class="card-title">Recent Activity</h4>
                    <span class="card-icon">📅</span>
                </div>
                <div class="activity-list">
                    <div class="activity-item">
                        <div class="activity-icon">🎯</div>
                        <div class="activity-content">
                            <div class="activity-title">PPT Hero Advanced Level</div>
                            <div class="activity-time">2 hours ago</div>
                        </div>
                        <div class="activity-score">+450 pts</div>
                    </div>
                    <div class="activity-item">
                        <div class="activity-icon">🧠</div>
                        <div class="activity-content">
                            <div class="activity-title">Philosophy Flow Session</div>
                            <div class="activity-time">1 day ago</div>
                        </div>
                        <div class="activity-score">+200 pts</div>
                    </div>
                </div>
            </div>
        `;
    }

    createMobileGameBreakdown() {
        return Object.entries(this.analyticsData.games).map(([gameId, data]) => `
            <div class="game-breakdown-item">
                <div class="game-breakdown-header">
                    <span class="game-breakdown-icon">${gameId === 'pptHero' ? '🎯' : '🧠'}</span>
                    <div class="game-breakdown-info">
                        <h4 class="game-breakdown-title">${gameId === 'pptHero' ? 'PPT Hero' : 'Philosophy Flow'}</h4>
                        <p class="game-breakdown-status">${data.progression}</p>
                    </div>
                    <div class="game-breakdown-score">${data.bestScore.toLocaleString()}</div>
                </div>
                <div class="game-breakdown-stats">
                    <div class="breakdown-stat">
                        <span class="stat-label">Sessions</span>
                        <span class="stat-value">${data.totalSessions}</span>
                    </div>
                    <div class="breakdown-stat">
                        <span class="stat-label">Weekly</span>
                        <span class="stat-value">${data.weeklyActivity}</span>
                    </div>
                </div>
            </div>
        `).join('');
    }

    createInsights() {
        return this.analyticsData.insights.map(insight => `
            <div class="insight-card ${insight.impact}">
                <div class="insight-header">
                    <span class="insight-icon">${this.getInsightIcon(insight.type)}</span>
                    <h4 class="insight-title">${insight.title}</h4>
                </div>
                <p class="insight-description">${insight.description}</p>
                <div class="insight-impact ${insight.impact}">${insight.impact.toUpperCase()} IMPACT</div>
            </div>
        `).join('');
    }

    createMobileInsights() {
        return this.createInsights(); // Same structure for mobile
    }

    getInsightIcon(type) {
        switch (type) {
            case 'achievement': return '🏆';
            case 'improvement': return '📈';
            case 'warning': return '⚠️';
            case 'suggestion': return '💡';
            default: return '📊';
        }
    }

    setupEventListeners() {
        // NOTE: Navigation events are handled by bindExactDashboardEvents() - no duplicate binding here

        // Period selector
        this.domElement.querySelectorAll('[data-field="period"]').forEach(selector => {
            selector.addEventListener('change', (e) => {
                this.handlePeriodChange(e.target.value);
            });
        });
    }

    // Removed conflicting handleAction method - using handleTemplateAction instead

    handlePeriodChange(period) {
        this.currentPeriod = period;
        console.log('📊 Period changed to:', period);
        // Reload analytics data for new period
        this.loadAnalyticsData().then(() => {
            this.refreshAnalyticsContent();
        });
    }

    exportData() {
        console.log('📊 Exporting analytics data...');
        this.showNotification('Analytics data exported successfully!', 'success');
    }

    showPeriodSelector() {
        // Mobile period selector modal
        console.log('📱 Showing period selector');
    }

    refreshAnalyticsContent() {
        // Refresh the analytics content with new data
        const content = this.domElement.querySelector('.analytics-content');
        if (content) {
            // Update overview cards, charts, etc.
            const overviewGrid = content.querySelector('.overview-grid');
            if (overviewGrid) {
                overviewGrid.innerHTML = this.createOverviewCards();
            }
        }
    }

    async openHallOfFame() {
        console.log('🏆 Opening new desktop Hall of Fame template...');
        this.startScene('HallOfFameDesktopTemplate');
    }

    async handleLogout() {
        try {
            await auth.signOut();
            this.startScene('GuestMenuSceneDOM');
        } catch (error) {
            console.error('Logout error:', error);
            this.showNotification('Error logging out. Please try again.', 'error');
        }
    }

    showNotification(message, type = 'info') {
        console.log(`📊 Notification (${type}):`, message);
        if (window.showGeneralMessage) {
            window.showGeneralMessage(message, 3000, type === 'error');
        }
    }

    shutdown() {
        console.log('📊 AnalyticsSceneDOM: Shutting down and cleaning up overlays...');
        
        if (this.domElement) {
            this.domElement.remove();
            this.domElement = null;
        }
        
        // Clean up analytics scene overlay
        const analyticsOverlay = document.getElementById('analytics-scene-overlay');
        if (analyticsOverlay) {
            analyticsOverlay.remove();
            console.log('📊 AnalyticsSceneDOM: Overlay removed');
        }
        
        super.shutdown();
    }
}
